import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/BlueprintFlipbook.css';

// PDF page images (extracted from actual Course 1 Blueprint PDF)
const BLUEPRINT_PAGES = [
  '/images/blueprint-pages/page-01.jpg', // Cover
  '/images/blueprint-pages/page-02.jpg', // Contents
  '/images/blueprint-pages/page-03.jpg', // Module intro
  '/images/blueprint-pages/page-04.jpg', // Module 1
  '/images/blueprint-pages/page-05.jpg', // Module 2
  '/images/blueprint-pages/page-06.jpg', // Module 3
  '/images/blueprint-pages/page-07.jpg', // Module 4
  '/images/blueprint-pages/page-08.jpg', // Module 5
];

const BlueprintFlipbook = ({ isActive, onReady }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const flipIntervalRef = useRef(null);
  const flipTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Preload all images
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = BLUEPRINT_PAGES.length;

    BLUEPRINT_PAGES.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
          if (onReady) onReady();
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
          if (onReady) onReady();
        }
      };
      img.src = src;
    });
  }, [onReady]);

  // Handle page flip animation
  const flipToNextPage = useCallback(() => {
    if (prefersReducedMotion) return;
    
    setIsFlipping(true);
    
    // After flip animation completes, move to next page
    flipTimeoutRef.current = setTimeout(() => {
      setCurrentPage((prev) => (prev + 1) % BLUEPRINT_PAGES.length);
      setIsFlipping(false);
    }, 1200); // Match CSS animation duration
  }, [prefersReducedMotion]);

  // Start/stop flip loop based on active state
  useEffect(() => {
    if (isActive && imagesLoaded && !prefersReducedMotion) {
      // Start flipping after a short delay
      flipTimeoutRef.current = setTimeout(() => {
        flipToNextPage();
        // Continue flipping every 2 seconds (1.2s flip + 0.8s pause)
        flipIntervalRef.current = setInterval(flipToNextPage, 2000);
      }, 800);
    } else {
      // Stop flipping
      if (flipIntervalRef.current) {
        clearInterval(flipIntervalRef.current);
        flipIntervalRef.current = null;
      }
      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
        flipTimeoutRef.current = null;
      }
      // Reset to cover when deactivated
      if (!isActive) {
        setCurrentPage(0);
        setIsFlipping(false);
      }
    }

    return () => {
      if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);
      if (flipTimeoutRef.current) clearTimeout(flipTimeoutRef.current);
    };
  }, [isActive, imagesLoaded, prefersReducedMotion, flipToNextPage]);

  // Get the next page index for flip animation
  const nextPage = (currentPage + 1) % BLUEPRINT_PAGES.length;

  // Reduced motion: show static preview with stacked pages
  if (prefersReducedMotion) {
    return (
      <div className="flipbook-container reduced-motion" ref={containerRef}>
        <div className="flipbook-static">
          <div className="static-page-stack">
            <div className="stack-page stack-3"></div>
            <div className="stack-page stack-2"></div>
            <div className="stack-page stack-1"></div>
          </div>
          <img 
            src={BLUEPRINT_PAGES[0]} 
            alt="Course 1 Blueprint Cover" 
            className="flipbook-cover-static"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flipbook-container" ref={containerRef}>
      {!imagesLoaded && (
        <div className="flipbook-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      <div className={`flipbook ${imagesLoaded ? 'loaded' : ''}`}>
        {/* Page stack (background pages) */}
        <div className="page-stack">
          <div className="stack-page stack-4"></div>
          <div className="stack-page stack-3"></div>
          <div className="stack-page stack-2"></div>
        </div>

        {/* Current page (bottom) */}
        <div className="flipbook-page current-page">
          <img 
            src={BLUEPRINT_PAGES[nextPage]} 
            alt={`Blueprint page ${nextPage + 1}`}
            draggable={false}
          />
        </div>

        {/* Flipping page (top, animates) */}
        <div className={`flipbook-page flipping-page ${isFlipping ? 'flip' : ''}`}>
          <div className="page-front">
            <img 
              src={BLUEPRINT_PAGES[currentPage]} 
              alt={`Blueprint page ${currentPage + 1}`}
              draggable={false}
            />
          </div>
          <div className="page-back">
            <img 
              src={BLUEPRINT_PAGES[nextPage]} 
              alt={`Blueprint page ${nextPage + 1}`}
              draggable={false}
            />
          </div>
        </div>

        {/* Page shadow during flip */}
        <div className={`flip-shadow ${isFlipping ? 'active' : ''}`}></div>
      </div>
    </div>
  );
};

export default BlueprintFlipbook;
